import { Inject, Injectable, LOCALE_ID } from '@angular/core';

// Rx JS imports
import { Observable, of, forkJoin } from 'rxjs';
import { ReplaySubject } from 'rxjs';
import { map, first, take, pluck, debounceTime, shareReplay, switchMap, startWith, distinctUntilChanged, catchError } from 'rxjs/operators';

// Models
import { Project } from '../models/project';
import { ListenEvent } from './database';
import { DatabaseService } from './database.service';
import { BillingType, Coords, Language, Mote, MotesPaths, MoteTranslation, PartnerCostTransferType, PlanType, TranslationMap } from '../models';
import { MoteService } from './mote.service';
import { LocalStorage } from 'ngx-webstorage';
import { LanguageService } from './language.service';
import { UserStateService } from './user-state.service';

export type ProjectChangeEv = { project: Project, tour: Mote, projectChanged: boolean, routeChanged: boolean };
export type TourDetails = { project: Project, tour: Mote, children: Mote[], translations: MoteTranslation[]; projectMotes: Mote[] }
export type ProjectToursEv = { project: Project, originTours: Mote[], translations: TranslationMap; tours: Mote[]; }

@Injectable({ providedIn: 'root' })
export class ProjectService {

    public activeProject: Project = null;
    public activeTour: Mote = null;
    private activeProjectSubject: ReplaySubject<ProjectChangeEv> = new ReplaySubject(1);
    private activeStorySubject: ReplaySubject<string> = new ReplaySubject(1);

    @LocalStorage('preferredLanguage', Language.en) preferredLanguage: Language;
    public languageSubject: ReplaySubject<Language> = new ReplaySubject(1);

    constructor(private af: DatabaseService,
        private moteService: MoteService,
        private userStateService: UserStateService,
        @Inject(LOCALE_ID) public locale: string) {
        this.languageSubject.next(this.preferredLanguage);
    }

    public async init(activeProjectKey: string = "", activeRouteKey: string = ""): Promise<ProjectChangeEv> {
        if(!activeProjectKey) return null;
        return this.changeActiveProject(activeProjectKey, activeRouteKey);
    }

    public saveProject(project: Project, uid: string = ""): Promise<boolean> {
        // Get new id for mote
        if(!project.key) {
            // GeneractiveProjectate new key
            project.key = this.af.database.ref().push().key;
        }
        if(!project.createdOn) project.createdOn = this.af.serverTime();
        if(!project.image.createdOn) project.image.createdOn = this.af.serverTime();
        if(!project.image.createdBy) project.image.createdBy = uid;
        if(!project.image.project) project.image.project = project.key;

        // Create database reference and update moteContent
        return this.af.database.ref(`/projects/${project.key}`).update(project.toJson()).then(() => true).catch((err) => {
            console.log("Could not save project: ", err);
            return false;
        });;
    }

    /**
     * Update the `position` property of a `Project`
     * @param projectKey Key for the project
     * @param position The new position
     * @returns A `Promise` which resolves once the update on the server is complete
     */
    public saveProjectPosition(projectKey: string, position: Coords): Promise<void> {
        return this.af.database.ref(`projects/${projectKey}`).update({ 'position': position.toJson() });
    }

    /**
     * Set deletedOn timestamp in order to delete the project delayed and unpublish project
     * (will be delete for real on server side on time schedule basis).
     * Set all motes of the project to be deleted as well.
     */
    public async deleteProject(projectKey: string): Promise<boolean> {
        if(!projectKey) return false;

        // Delete all motes in project
        const motesDeleted = await this.moteService.deleteProjectMotes(projectKey);
        if(!motesDeleted) return false;

        // Remove published flag from project
        const publishedRef = this.getProjectPropertyRef(projectKey, 'published');
        await publishedRef.remove().then(() => true).catch(() => false);

        // Set deleted timestamp on project
        const deletedOnRef = this.getProjectPropertyRef(projectKey, 'deletedOn');
        return deletedOnRef.set(this.af.serverTime()).then(() => true).catch(() => false);
    }

    private getProjectPropertyRef(projectKey: string, property: keyof Project) {
        return this.af.database.ref(`projects/${projectKey}/${property}`);
    }

    public watchAllProjects(): Observable<Project[]> {
        const CHANGE_EVENTS: Array<ListenEvent> = [ListenEvent.added, ListenEvent.removed, ListenEvent.changed, ListenEvent.moved];
        // Get new list observable (use throttle time as firebase fires a lot )
        return this.af.list("/projects").snapshotChanges(CHANGE_EVENTS).pipe(debounceTime(300), map((results) => {
            // Create array of Project objects from firebase object array
            let list = results.map((json) => {
                return new Project(json.key, json.payload.val());
            }).filter( project => !project.deletedOn );
            return list.sort((a, b) => (a.order - b.order));
        }));
    }

    public watchPublishedProjects(): Observable<Project[]> {
        const CHANGE_EVENTS: Array<ListenEvent> = [ListenEvent.added, ListenEvent.removed, ListenEvent.changed, ListenEvent.moved];
        // Get new list observable
        let query = q => q.orderByChild('published').equalTo(true);
        // OBS: Use throttle time with 500 ms of delay, because otherwise firebase will return multiple times with this list with only very few elements
        return this.af.list("projects", query).snapshotChanges(CHANGE_EVENTS).pipe(debounceTime(300), map((results) => {
            // Create array of Project objects from firebase object array
            let list = results.map((json) => {
                return new Project(json.key, json.payload.val());
            });
            return list.sort((a, b) => (a.order - b.order));
        }));
    }

    public watchProject(projectKey: string): Observable<Project | null> {
        // Get mote from DB and mapt to Mote object
        return this.af.object('projects/' + projectKey).snapshotChanges().pipe(map(snapshot => {
            // Check if snapshot has valid payload
            if(!snapshot?.payload?.val) {
                console.warn(`Could not get project: '${projectKey}' from database, does project exist?`);
                return null;
            }
            // Create project
            const project = new Project(snapshot.key, snapshot.payload.val());
            // Return project if it has not been deleted
            return (project.deletedOn) ? null : project;
        }), shareReplay());
    }

    public getProject(projectKey: string): Promise<Project | null> {
        if(!projectKey) return Promise.resolve(null);
        return this.watchProject(projectKey).pipe(first()).toPromise();
    }

    public changeActiveProject(projectKey: string, activeRouteKey: string = "", previewKey: string = "", activatePublishedTour: boolean = true): Promise<ProjectChangeEv> {
        // TODO: Remove activatePublishedTour >hack< when app intro flow has been refactored

        return new Promise(async (resolve, reject) => {

            let isSameProject = !!(this.activeProject?.key && (this.activeProject?.key == projectKey));

            // Get project from db or cached
            let project = (isSameProject) ? this.activeProject : await this.getProject(projectKey);

            // If no or invalid project
            if(!project || !project.key) {
                return resolve({ project: null, tour: null, projectChanged: false, routeChanged: false });
            }

            // If project is default, update default route with locale
            if(projectKey == MotesPaths.DEFAULT_PROJECT_KEY) activeRouteKey = MotesPaths.DEFAULT_TOUR_KEY;

            // Use stored previewKey if this is the same tour (in case user reloads during tour preview)
            const activeTour = this.userStateService.getStoredActiveTour();
            if(!previewKey && activeTour.previewKey && (activeTour.tourKey == activeRouteKey)) previewKey = activeTour.previewKey;

            // Fetch route
            let tour = await this.moteService.getMotePreview(activeRouteKey, previewKey);

            // If no or invalid route, use first of project
            if(activatePublishedTour && (!tour || !tour.doesExist)) {
                let routes = await this.moteService.watchPublishedTours(projectKey).pipe(take(1)).toPromise();
                tour = (routes.length > 0) ? routes[0] : null;
            }

            let isSameRoute = (project.activeRouteKey == tour?.key);

            // Populate active route key within project
            project.activeRouteKey = (tour && tour.doesExist) ? tour.key : "";
            // Populate active preview key if used to access tour
            project.activePreviewKey = (tour && tour.doesExist && previewKey) ? previewKey : "";
            // Save active project / tour in local storage
            this.userStateService.storeActiveTour(project.key, project.activeRouteKey, project.activePreviewKey);
            // Save project and tour for fast access
            this.activeProject = project;
            this.activeTour = tour;

            let projectEv = { project: this.activeProject, tour: this.activeTour, projectChanged: !isSameProject, routeChanged: !isSameRoute, previewKey };

            // Notify listners
            if(!isSameProject || !isSameRoute) this.activeProjectSubject.next(projectEv);
            resolve(projectEv);
        });
    }

    public async getActiveTour(): Promise<Mote> {
        // If a active project and hench an active tour is already retrieved from DB, just return the local reference
        if(this.activeTour) return this.activeTour;

        // In case no active project is activated yet, await an active project to be selected before returning active tour
        let activeProject = await this.getActiveProject();
        return this.activeTour;
    }

    public watchActiveProject(): ReplaySubject<ProjectChangeEv> {
        return this.activeProjectSubject;
    }

    public getActiveProject(): Promise<Project> {
        return this.watchActiveProject().pipe(pluck('project'), take(1)).toPromise();
    }

    /**
     * Watch all projects that user is member of
     */
    public watchMemberProjects(userKey: string): Observable<Project[]> {
        return this.watchAllProjects().pipe(map((projects: Project[]) => {
            if(!userKey) return [];
            // Filter out projects where user is not a member
            let userProjects = projects.filter((project: Project) => (project.owners[userKey] && !project.deletedOn));
            return userProjects;
        }));
    }

    /**
     * Get all projects that user is member of
     */
    public getMemberProjects(userKey: string): Promise<Project[]> {
        return this.watchMemberProjects(userKey).pipe(first()).toPromise();
    }

    /**
     * Watch all projects that user owns
     */
    public watchOwnerProjects(userKey: string): Observable<Project[]> {
        if(!userKey) return of([]);
        let query = (ref) => ref.orderByChild('owner').equalTo(userKey);

        return this.af.database.list('projects', query).snapshotChanges().pipe(map(snapList => {
            return snapList.map((snap) => new Project(snap.key, snap.payload.val()));
        }));
    }

    /**
     * Get all projects that user owns
     */
    public getOwnerProjects(userKey: string): Promise<Project[]> {
        return this.watchOwnerProjects(userKey).pipe(first()).toPromise();
    }

    public removeProjectOwner(projectKey: string, userKey: string = null) {
        // TODO Get projects from local or DB
        let ref = this.af.object("projects/" + projectKey + "/owners/" + userKey);
        ref.remove();
    }

    public addProjectOwner(projectKey: string, userKey: string = null) {
        // TODO Get projects from local or DB
        let ref = this.af.object("projects/" + projectKey + "/owners/" + userKey);
        ref.update({ createdOn: this.af.serverTime() });
    }

    public getAllProjectKeys(): Promise<string[]> {
        return this.watchAllProjects().pipe(first()).pipe(map(projects => projects.map(p => p.key))).toPromise();
    }

    /**
     * Prefix search: RTDB range queries on `title` (lowercased + first-letter capitalized) plus `orderByKey` on project id.
     * Does not watch the full `/projects` list.
     */
    public searchProjectsByTitlePrefixFanOut(term: string, maxResults: number = 5): Observable<Project[]> {
        const trimmed = term.trim();
        if(trimmed.length < 3) return of([]);

        const lower = trimmed.toLowerCase();
        const capitalized = trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
        const titlePrefixes = lower === capitalized ? [lower] : [lower, capitalized];

        const titleQueries = titlePrefixes.map((p) => this.queryProjectsByTitlePrefix(p, maxResults));
        const keyQuery = this.queryProjectsByKeyPrefix(trimmed, maxResults);
        return forkJoin([...titleQueries, keyQuery]).pipe(
            map((arrays) => this.mergeDedupeProjectsByKey(arrays.flat(), maxResults))
        );
    }

    private queryProjectsByTitlePrefix(prefix: string, limit: number): Observable<Project[]> {
        const end = prefix + '\uf8ff';
        const query = (ref) => ref.orderByChild('title').startAt(prefix).endAt(end).limitToFirst(limit);
        return this.af.list('projects', query).snapshotChanges().pipe(
            take(1),
            map((results: { key: string, payload: { val: () => any } }[]) => {
                if(!results || results.length <= 0) return [];
                return results.map((json) => new Project(json.key, json.payload.val())).filter((p) => !p.deletedOn);
            }),
            catchError(() => of([]))
        );
    }

    private queryProjectsByKeyPrefix(prefix: string, limit: number): Observable<Project[]> {
        const end = prefix + '\uf8ff';
        const query = (ref) => ref.orderByKey().startAt(prefix).endAt(end).limitToFirst(limit);
        return this.af.list('projects', query).snapshotChanges().pipe(
            take(1),
            map((results: { key: string, payload: { val: () => any } }[]) => {
                if(!results || results.length <= 0) return [];
                return results.map((json) => new Project(json.key, json.payload.val())).filter((p) => !p.deletedOn);
            }),
            catchError(() => of([]))
        );
    }

    private mergeDedupeProjectsByKey(projects: Project[], max: number): Project[] {
        const byKey = new Map<string, Project>();
        for(const p of projects) {
            if(!byKey.has(p.key)) byKey.set(p.key, p);
        }
        return Array.from(byKey.values())
            .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
            .slice(0, max);
    }

    public watchClientConfig(configKey: string) {
        return this.af.object("config/" + configKey).snapshotChanges().pipe(map((config) => config.payload.val()));
    }

    public saveClientConfig(configKey: string, configJson: any) {
        return this.af.database.ref(`config/${configKey}`).update(configJson);
    }

    public setPreferredLanguage(language: Language) {
        // Persist preferred language in local storage
        this.preferredLanguage = language;
        // Notify listeners
        this.languageSubject.next(language);
    }

    public getPreferredLanguage(): string {
        // Notify listeners
        return this.preferredLanguage || Language.en;
    }

    public readLanguageSetting(): Language {
        this.languageSubject.next(this.preferredLanguage);
        return this.preferredLanguage;
    }

    public watchProjectTours(locale: string = this.locale): Observable<ProjectToursEv> {
        let project: Project;

        return this.watchActiveProject().pipe(switchMap((ev) => {
            if(!ev?.project?.key) return of([]);
            project = ev.project;
            // Watch tours in project sorted by updatedOn
            return this.moteService.watchLatestTours(project.key);
        })).pipe(map((tours: Mote[]) => {
            // Obs: This is not a list of origin tours, but there will be one tour per origin and the tour will be represented by the chosen 'locale' language if available
            let originTours = LanguageService.filterMotesByLocale(tours, locale);
            let translations = LanguageService.getTranslationMap(tours);
            return { project, originTours, translations, tours };
        }));
    }

    public watchActiveTourDetails(showDeleted: boolean = false): Observable<TourDetails> {
        let project: Project;

        return this.watchActiveProject().pipe(switchMap((ev) => {
            if(!ev?.project || !this.activeTour) return of([]);
            project = ev.project;
            // Watch project motes of active project. The debounceTime is not needed, but will decrease the amount of updates
            // when moving a story / feature points from 10 updates to around 5 (increasing it more will decrease user experience).
            return this.moteService.watchProjectMotes([ev.project.key], showDeleted).pipe(debounceTime(50));
        })).pipe(map((projectMotes) => {
            if(!projectMotes || projectMotes.length <= 0) return null;
            let projectTours = projectMotes.filter(mote => mote.canHaveChildren());
            let tourChildren = projectMotes.filter(mote => mote.parent == this.activeTour.key);
            let translationMap = LanguageService.getSimpleTranslationMap(projectTours);
            let tourTranslations = translationMap[this.activeTour.originKey || this.activeTour.key];
            return { project: project, tour: this.activeTour, children: tourChildren, translations: tourTranslations, projectMotes: projectMotes };
        }));
    }

    /**
     * Set the selected/active story of the project and distribute it to listeners
     */
    changeActiveStory(storyKey: string) {
        this.activeStorySubject.next(storyKey);
    }

    /**
     * Watch the selected/active story selected by the changeActiveStory function.
     * Retrieves the story from the database or empty observable if no story is selected.
     */
    watchActiveStory(): Observable<Mote> {
        return this.activeStorySubject.pipe(switchMap(storyKey => {
            return this.moteService.watchMote(storyKey);
        }));
    }

    public async changeCreatorPlan(project: Project, newPlan: PlanType): Promise<void> {
        if(!project) return;
        project.billingType = (newPlan == PlanType.PERSONAL) ? null : BillingType.MONTHLY;
        project.creatorPlan = newPlan;
        project.planChangedOn = this.af.serverTime();
        // Mark world published for payed plans in order to allow tour publishing
        project.published = (newPlan != PlanType.PERSONAL);
        await this.saveProject(project);
    }

    public async changePartnerCostTransferType(projectKey: string, transferType: PartnerCostTransferType): Promise<boolean> {
        if(!projectKey) return false;

        return this.af.database.ref(`/projects/${projectKey}/partnerCostTransferType`).set(transferType).then(() => true).catch((err) => {
            console.log("Could not save project transfer type: ", err);
            return false;
        });;
    }

    public async userHasAccess(userKey: string, projectKey: string): Promise<boolean> {
        let isDeleted = await this.af.database.ref(`/projects/${projectKey}/deletedOn`).once('value');
        let owner = await this.af.database.ref(`/projects/${projectKey}/owners/${userKey}`).once('value');
        return Boolean(owner.val() && !isDeleted.val());
    }

    public async getLatestUserProject(userKey: string): Promise<string> {
        const activeTour = this.userStateService.getStoredActiveTour();
        // Get stored project if there is one
        if(activeTour.projectKey) return activeTour.projectKey;
        // Get first of user's projects if they have any
        let projects = await this.getMemberProjects(userKey);
        if(projects.length > 0) return projects[0].key;

        return null;
    }

    /**
     * Watch number of published tours of the active project
     */
    public watchPublishedTourCount(): Observable<number> {
        return this.watchActiveProject().pipe(
            switchMap(ev => {
                // Immediately emit 0 (reset tour count on project change), then emit the real tour count
                return this.moteService.watchPublishedTours(ev.project?.key).pipe(startWith([]));
            }),
            map((tours) => {
                // Count published tours (and disregard translations)
                return tours.filter(tour => !tour.originKey).length;
            }),
            distinctUntilChanged());
    }

}
